import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Mail, Copy, Check } from 'lucide-react';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from 'src/components/ui/hover-card';
import { cn } from 'src/lib/utils';
import { getActionComponent } from '../actions';
import {
  EMAIL_STATUS_META,
  emailStatusFrom,
  toneClasses,
  toneHeaderClasses,
  toneHeaderTextClasses,
} from './contactStatus';
import { relativeTimeLabel } from './tabFormatters';

const ResendContactLinkAction = getActionComponent(
  'senior_level_flow',
  'resend_contact_link'
);

// Icon-only copy-to-clipboard, same `copied` + setTimeout state machine
// CandidatePreviewModal.jsx's own copy buttons already use — just a
// lighter, inline-icon rendering suited to this popover's tight space
// rather than that one's full text+icon pill. stopPropagation matters here
// specifically because this sits inside a table row.
const CopyButton = ({ value, label }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e) => {
    e.stopPropagation();
    if (!value) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={label}
      className={cn(
        'shrink-0 p-[3px] rounded transition-colors',
        copied
          ? 'text-ds-success'
          : 'text-muted-foreground hover:text-foreground-strong'
      )}
    >
      {copied ? (
        <Check className="w-3 h-3" strokeWidth={2.4} />
      ) : (
        <Copy className="w-3 h-3" strokeWidth={2} />
      )}
    </button>
  );
};

CopyButton.propTypes = {
  value: PropTypes.string,
  label: PropTypes.string.isRequired,
};

CopyButton.defaultProps = {
  value: null,
};

// Contact Status column, Contact tab — reads the real
// row.notifications.status.email flags (see contactStatus.js for the full
// contract). Email-only: the real candidate-list response has no
// whatsapp/sms notification data at all yet, and per explicit instruction
// those two chips are dropped rather than shown against fabricated status —
// this used to render all three as a row of icon chips off a mocked
// row.contact_status; that mock is gone now that the real field shipped.
// A HoverCard (not a plain Tooltip) for the same reason as before: it opens
// on hover/focus but keeps a grace period as the pointer crosses from the
// trigger into the card, so the "Resend Link" button inside stays
// reachable — a plain Tooltip closes the instant the pointer leaves the
// trigger, before it could ever reach a button inside.
const ContactStatusIcons = ({ row, onActionSuccess }) => {
  const emailFlags = row?.notifications?.status?.email;
  const statusKey = emailStatusFrom(emailFlags, row?.contact_link_send_at);

  if (!statusKey) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }

  const info = EMAIL_STATUS_META[statusKey];
  const identity = row?.email || null;
  const sentRelative = relativeTimeLabel(row?.contact_link_send_at);

  return (
    <HoverCard openDelay={150} closeDelay={150}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          aria-label={`Email — ${info.label}`}
          className={cn(
            'w-6 h-6 rounded-md flex items-center justify-center transition-opacity hover:opacity-80',
            toneClasses(info.tone)
          )}
        >
          <Mail className="w-3.5 h-3.5" strokeWidth={2} />
        </button>
      </HoverCardTrigger>
      <HoverCardContent className="w-64 p-0 overflow-hidden" align="end">
        {/* Colored header band carries the status itself — no separate pill
            needed once the backdrop already reads as "this is the status,"
            per design feedback. */}
        <div
          className={cn(
            'flex items-center justify-between gap-2 px-3.5 py-2.5',
            toneHeaderClasses(info.tone)
          )}
        >
          <span
            className={cn(
              'text-[12.5px] font-semibold',
              toneHeaderTextClasses(info.tone)
            )}
          >
            Email
          </span>
          <span
            className={cn(
              'text-[11px] font-bold',
              toneHeaderTextClasses(info.tone)
            )}
          >
            {info.label}
          </span>
        </div>
        <div className="p-3.5">
          {identity && (
            <div className="flex items-center gap-1 min-w-0">
              <p className="text-[12px] text-muted-foreground truncate font-mono">
                {identity}
              </p>
              <CopyButton value={identity} label="Copy email" />
            </div>
          )}
          <p className="text-[11.5px] text-muted-foreground mt-1 leading-snug">
            {info.detail}
          </p>
          {sentRelative && (
            <p className="text-[10.5px] text-muted-foreground/70 mt-1">
              Sent {sentRelative}
            </p>
          )}
          {/* Resending into an address that already reported this as spam
              would just repeat the offense — that address is done as a
              contact channel, not a "try again" situation. */}
          {ResendContactLinkAction && statusKey !== 'complained' && (
            <div className="mt-2.5 pt-2.5 border-t border-border">
              <ResendContactLinkAction
                actionKey="resend_contact_link"
                label="Resend Link"
                mappingIds={[row?.id]}
                onSuccess={onActionSuccess}
                renderAs="button"
              />
            </div>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

ContactStatusIcons.propTypes = {
  row: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    email: PropTypes.string,
    contact_link_send_at: PropTypes.string,
    notifications: PropTypes.shape({
      status: PropTypes.shape({
        email: PropTypes.shape({
          sent: PropTypes.bool,
          delivered: PropTypes.bool,
          seen: PropTypes.bool,
          engaged: PropTypes.bool,
          bounced: PropTypes.bool,
          failed: PropTypes.bool,
          complained: PropTypes.bool,
        }),
      }),
    }),
  }),
  onActionSuccess: PropTypes.func,
};

ContactStatusIcons.defaultProps = {
  row: null,
  onActionSuccess: () => {},
};

export default ContactStatusIcons;
